/**
 * Phone Number Formatting Utilities
 * Formats phone numbers to (xxx) xxx-xxxx format
 */

/**
 * Formats a phone number to (xxx) xxx-xxxx format
 * @param {string} phoneNumber - The phone number to format
 * @returns {string} - Formatted phone number
 */
export function formatPhoneNumber(phoneNumber) {
  if (!phoneNumber) return '';

  // Remove all non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, '');

  // Handle different phone number lengths
  let phoneDigits = cleaned;

  // If it's 11 digits and starts with 1 (US country code), remove the 1
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    phoneDigits = cleaned.slice(1);
  }
  // If it's more than 10 digits, take the last 10 digits
  else if (cleaned.length > 10) {
    phoneDigits = cleaned.slice(-10);
  }
  // If it's less than 10 digits, use as is
  else if (cleaned.length < 10) {
    phoneDigits = cleaned;
  }

  // Format based on length
  if (phoneDigits.length === 0) return '';
  if (phoneDigits.length <= 3) return `(${phoneDigits}`;
  if (phoneDigits.length <= 6) return `(${phoneDigits.slice(0, 3)}) ${phoneDigits.slice(3)}`;
  return `(${phoneDigits.slice(0, 3)}) ${phoneDigits.slice(3, 6)}-${phoneDigits.slice(6)}`;
}

/**
 * Handles phone number input formatting as user types
 * @param {Event} event - The input event
 * @param {Function} setFieldValue - Formik setFieldValue function
 * @param {string} fieldName - The field name (default: 'phoneNumber')
 */
export function handlePhoneNumberInput(event, setFieldValue, fieldName = 'phoneNumber') {
  const input = event.target.value;
  const formatted = formatPhoneNumber(input);
  setFieldValue(fieldName, formatted);
}

/**
 * Validates phone number format
 * @param {string} phoneNumber - The phone number to validate
 * @returns {boolean} - True if valid format
 */
export function validatePhoneNumber(phoneNumber) {
  if (!phoneNumber) return false;

  // Remove all non-digit characters and check if it's exactly 10 digits
  const cleaned = phoneNumber.replace(/\D/g, '');
  return cleaned.length === 10;
}

/**
 * Handles key press for phone number input (allows only digits, parentheses, spaces, and hyphens)
 * @param {Event} event - The key press event
 */
export function handlePhoneKeyPress(event) {
  const allowedCharacters = /[0-9()\s-]/;
  const key = event.key;

  // Allow backspace, delete, tab, escape, enter
  if (['Backspace', 'Delete', 'Tab', 'Escape', 'Enter'].includes(key)) {
    return;
  }

  if (!allowedCharacters.test(key)) {
    event.preventDefault();
  }
}

/**
 * Gets the maximum length for phone number input (including formatting characters)
 * @returns {number} - Maximum length (14 for (xxx) xxx-xxxx)
 */
export function getPhoneMaxLength() {
  return 14; // (xxx) xxx-xxxx
}

/**
 * Cleans phone number for storage (removes formatting)
 * @param {string} phoneNumber - The formatted phone number
 * @returns {string} - Clean phone number with only digits
 */
export function cleanPhoneNumber(phoneNumber) {
  if (!phoneNumber) return '';

  // Remove all non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, '');

  // If it's 11 digits and starts with 1 (US country code), remove the 1
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return cleaned.slice(1);
  }
  // If it's more than 10 digits, take the last 10 digits
  else if (cleaned.length > 10) {
    return cleaned.slice(-10);
  }
  // Otherwise return as is
  return cleaned;
}
